import * as React from "react";

import { t } from "@impero-com/common/utils/i18n";
import { Divider, InfoTooltip } from "@impero-com/ui";
import memoizeOne from "memoize-one";

import {
    SubsequentStepIndex,
    HalfYear,
    SchedulingOccurrenceChangeForDueDateOffsetForMonthOffsetPeriod as SchedulingOccurrenceChange,
    UpdatableRecurrenceRuleWithOffsetAndFinancialYearForHalfYearAndDueDateOffsetForMonthOffsetPeriodAndUpdatableDueDateOffsetForUpdatableMonthOffsetPeriod as UpdatableHalfYearlyRecurrenceRule,
    FinancialYearView,
} from "api/generated/model";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import { isNotUndefined } from "utils/typePredicates";

import { PeriodType } from "../common/multiDefaultDueDateOffsetInput";
import { MultiFinancialYearCheckbox } from "../common/multiFinancialYearCheckbox";
import {
    GenericOccurrencesArray,
    HalfYearlyRecurrenceRuleWithArrays,
} from "../common/rulesWithArrays";

import { MultiHalfYearlyCheckboxes } from "./multiHalfYearlyCheckboxes";

const toHalfYearlyArrayRecords = memoizeOne(
    (
        occurrences: GenericOccurrencesArray<HalfYear>
    ): Record<HalfYear, boolean[]> => {
        return {
            First: occurrences.First.map(isNotUndefined),
            Second: occurrences.Second.map(isNotUndefined),
        };
    }
);
abstract class BaseHalfYearlyRecurrenceRuleUpdaterForm<
    P,
> extends React.PureComponent<P> {
    render = () => {
        return (
            <>
                <Divider />
                {this.buildDefaultDueDateOffsetInput()}
                <Divider />
                <h1>
                    {t("CreateControl_Planning_Title")}&nbsp;
                    <InfoTooltip
                        text={t("Control_ModifySchedule_PlanningInfo")}
                    />
                </h1>
                {this.buildResponsibleAllocatedTimeInputs()}
                {this.buildReviewerAllocatedTimeInputs("SubsequentStep0")}
                {this.buildReviewerAllocatedTimeInputs("SubsequentStep1")}
                {this.buildFinancialYearCheckbox()}
                <Divider />
                {this.buildHalfYearly()}
                {this.buildCustomDueDateDialog()}
                {this.buildRemindersWidget()}
            </>
        );
    };

    abstract buildDefaultDueDateOffsetInput(): React.ReactNode;

    abstract buildResponsibleAllocatedTimeInputs(): React.ReactNode;

    abstract buildReviewerAllocatedTimeInputs(
        subsequentStepIndex: SubsequentStepIndex
    ): React.ReactNode;

    abstract buildFinancialYearCheckbox(): React.ReactNode;

    abstract buildHalfYearly(): React.ReactNode;

    abstract buildCustomDueDateDialog(): React.ReactNode;

    abstract buildRemindersWidget(): React.ReactNode;
}

class PartialHalfYearlyRecurrenceRuleUpdater extends BaseHalfYearlyRecurrenceRuleUpdaterForm<PartialHalfYearlyRecurrenceRuleUpdaterProps> {
    buildDefaultDueDateOffsetInput = () =>
        this.props.buildDefaultDueDateOffsetInput("EDIT", "HalfYear");

    buildResponsibleAllocatedTimeInputs = () =>
        this.props.buildResponsibleAllocatedTimeInputs("EDIT");

    buildReviewerAllocatedTimeInputs = (
        subsequentStepIndex: SubsequentStepIndex
    ) =>
        this.props.buildReviewerAllocatedTimeInputs(
            "EDIT",
            subsequentStepIndex
        );

    buildFinancialYearCheckbox = () => {
        const { financialYear, currentRule } = this.props;

        if (financialYear) {
            const values = currentRule.financialYearId?.map((id) => !!id);
            return (
                <MultiFinancialYearCheckbox
                    type="EDIT"
                    values={values}
                    onChange={this.handleFinancialYearChange}
                />
            );
        } else {
            return <></>;
        }
    };

    private handleFinancialYearChange = (checked: boolean) => {
        const { financialYear, onChangeFinancialYear } = this.props;
        let id = null;
        if (checked && financialYear) {
            id = financialYear.id;
        }
        onChangeFinancialYear(id);
    };

    buildHalfYearly = () => {
        const {
            currentRule: { occurrences },
        } = this.props;
        const values = toHalfYearlyArrayRecords(occurrences);
        return (
            <BaseFormDisplay label={t("Control_Scheduling_Advanced")}>
                <MultiHalfYearlyCheckboxes
                    type="EDIT"
                    halfYears={values}
                    onChange={this.onChangeHalfYearly}
                />
            </BaseFormDisplay>
        );
    };

    onChangeHalfYearly = (key: HalfYear, value: boolean) => {
        const { onChangeHalfYearlyOccurrence } = this.props;
        onChangeHalfYearlyOccurrence(
            key,
            value ? "ActivateOnlyDeactivated" : undefined
        );
    };

    buildCustomDueDateDialog = () =>
        this.props.buildCustomDueDateDialog("EDIT");

    buildRemindersWidget = () => this.props.buildRemindersWidget("EDIT");
}

interface BasePartialHalfYearlyRecurrenceRuleProps {
    financialYear: FinancialYearView | null;
    currentRule: HalfYearlyRecurrenceRuleWithArrays;
    buildDefaultDueDateOffsetInput: (
        readOnly: "EDIT" | "READ_ONLY",
        period: PeriodType
    ) => React.ReactNode;
    buildResponsibleAllocatedTimeInputs: (
        readOnly: "EDIT" | "READ_ONLY"
    ) => React.ReactNode;
    buildReviewerAllocatedTimeInputs: (
        readOnly: "EDIT" | "READ_ONLY",
        subsequentStepIndex: SubsequentStepIndex
    ) => React.ReactNode;
    buildCustomDueDateDialog: (
        readOnly: "EDIT" | "READ_ONLY"
    ) => React.ReactNode;
    buildRemindersWidget: (readOnly: "EDIT" | "READ_ONLY") => React.ReactNode;
}

type PartialHalfYearlyRecurrenceRuleUpdaterProps =
    BasePartialHalfYearlyRecurrenceRuleProps &
        UpdatableHalfYearlyRecurrenceRule & {
            onChangeFinancialYear: (financialYearId: number | null) => void;
            onChangeHalfYearlyOccurrence: (
                interval: HalfYear,
                value: SchedulingOccurrenceChange | undefined
            ) => void;
        };

type PartialHalfYearlyRecurrenceRuleViewerProps =
    BasePartialHalfYearlyRecurrenceRuleProps;

class PartialHalfYearlyRecurrenceRuleViewer extends BaseHalfYearlyRecurrenceRuleUpdaterForm<PartialHalfYearlyRecurrenceRuleViewerProps> {
    buildDefaultDueDateOffsetInput = () =>
        this.props.buildDefaultDueDateOffsetInput("READ_ONLY", "HalfYear");

    buildResponsibleAllocatedTimeInputs = () =>
        this.props.buildResponsibleAllocatedTimeInputs("READ_ONLY");

    buildReviewerAllocatedTimeInputs = (
        subsequentStepIndex: SubsequentStepIndex
    ) =>
        this.props.buildReviewerAllocatedTimeInputs(
            "READ_ONLY",
            subsequentStepIndex
        );

    buildFinancialYearCheckbox = () => {
        const { financialYear, currentRule } = this.props;

        if (financialYear) {
            const values = currentRule.financialYearId.map((id) => !!id);
            return (
                <MultiFinancialYearCheckbox type="READ_ONLY" values={values} />
            );
        } else {
            return <></>;
        }
    };

    buildHalfYearly = () => {
        const {
            currentRule: { occurrences },
        } = this.props;
        const values = toHalfYearlyArrayRecords(occurrences);
        return (
            <BaseFormDisplay label={t("Control_Scheduling_Advanced")}>
                <MultiHalfYearlyCheckboxes
                    type="READ_ONLY"
                    halfYears={values}
                />
            </BaseFormDisplay>
        );
    };

    buildCustomDueDateDialog = () =>
        this.props.buildCustomDueDateDialog("READ_ONLY");

    buildRemindersWidget = () => this.props.buildRemindersWidget("READ_ONLY");
}

class FullHalfYearlyRecurrenceRuleUpdater extends BaseHalfYearlyRecurrenceRuleUpdaterForm<FullHalfYearlyRecurrenceRuleUpdaterProps> {
    buildDefaultDueDateOffsetInput = () =>
        this.props.buildDefaultDueDateOffsetInput("EDIT", "HalfYear");

    buildResponsibleAllocatedTimeInputs = () =>
        this.props.buildResponsibleAllocatedTimeInputs("EDIT");

    buildReviewerAllocatedTimeInputs = (
        subsequentStepIndex: SubsequentStepIndex
    ) =>
        this.props.buildReviewerAllocatedTimeInputs(
            "EDIT",
            subsequentStepIndex
        );

    buildFinancialYearCheckbox = () => {
        const { financialYear, currentRule } = this.props;

        if (financialYear) {
            const values = currentRule.financialYearId?.map((id) => !!id);
            return (
                <MultiFinancialYearCheckbox
                    type="EDIT"
                    values={values}
                    onChange={this.handleFinancialYearChange}
                />
            );
        } else {
            return <></>;
        }
    };

    private handleFinancialYearChange = (checked: boolean) => {
        const { financialYear, onChangeFinancialYear } = this.props;
        let id = null;
        if (checked && financialYear) {
            id = financialYear.id;
        }
        onChangeFinancialYear(id);
    };

    buildHalfYearly = () => {
        const {
            currentRule: { occurrences },
        } = this.props;
        const values = toHalfYearlyArrayRecords(occurrences);
        return (
            <BaseFormDisplay label={t("Control_Scheduling_Advanced")}>
                <MultiHalfYearlyCheckboxes
                    type="EDIT"
                    halfYears={values}
                    onChange={this.onChangeHalfYearly}
                />
            </BaseFormDisplay>
        );
    };

    onChangeHalfYearly = (key: HalfYear, value: boolean) => {
        const { onChangeHalfYearlyOccurrence } = this.props;
        onChangeHalfYearlyOccurrence(
            key,
            value ? "ActivateOnlyDeactivated" : undefined
        );
    };

    buildCustomDueDateDialog = () =>
        this.props.buildCustomDueDateDialog("EDIT");

    buildRemindersWidget = () => this.props.buildRemindersWidget("EDIT");
}

interface FullHalfYearlyRecurrenceRuleUpdaterProps {
    financialYear: FinancialYearView | null;
    currentRule: HalfYearlyRecurrenceRuleWithArrays;
    buildDefaultDueDateOffsetInput: (
        readOnly: "EDIT",
        period: PeriodType
    ) => React.ReactNode;
    buildResponsibleAllocatedTimeInputs: (readOnly: "EDIT") => React.ReactNode;
    buildReviewerAllocatedTimeInputs: (
        readOnly: "EDIT",
        subsequentStepIndex: SubsequentStepIndex
    ) => React.ReactNode;
    buildCustomDueDateDialog: (
        readOnly: "EDIT" | "READ_ONLY"
    ) => React.ReactNode;
    buildRemindersWidget: (readOnly: "EDIT") => React.ReactNode;
    onChangeFinancialYear: (financialYearId: number | null) => void;
    onChangeHalfYearlyOccurrence: (
        interval: HalfYear,
        value: SchedulingOccurrenceChange | undefined
    ) => void;
}
export type {
    BasePartialHalfYearlyRecurrenceRuleProps,
    PartialHalfYearlyRecurrenceRuleViewerProps,
    FullHalfYearlyRecurrenceRuleUpdaterProps,
};
export {
    PartialHalfYearlyRecurrenceRuleUpdater,
    PartialHalfYearlyRecurrenceRuleViewer,
    FullHalfYearlyRecurrenceRuleUpdater,
};
